
import UpsertReferralRuleRequest from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/Referral/UpsertReferralRuleRequest";
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
  buildUpsertReferralRulePayload,
  defaultReferralRuleUpsertTrigger,
  getRewardDefinition,
  getTriggerDefinition,
  isReferralRuleUpsertTriggerSupported,
  parseReferralRewardType,
  parseReferralTrigger,
  referralRewardTypeOptions,
  referralRuleUpsertTriggerOptions,
  ReferralRewardDefinition,
  ReferralTriggerDefinition,
} from "@/pages/page-referrals/referral-options";

@Component
export default class ReferralRuleFormModalComponent extends Vue {
  @Prop({ type: Boolean, default: false }) visible: boolean;
  @Prop({ type: Boolean, default: false }) isLoading: boolean;
  @Prop({ default: null }) value: UpsertReferralRuleRequest;

  form: UpsertReferralRuleRequest = new UpsertReferralRuleRequest();
  formRenderKey = 0;
  triggerOptions = referralRuleUpsertTriggerOptions;
  rewardTypeOptions = referralRewardTypeOptions;

  created() {
    this.initializeForm();
  }

  get modalTitle(): string {
    return this.isEditMode ? "Редактирование правила" : "Новое правило";
  }

  get isEditMode(): boolean {
    return this.form != null && this.form.RuleId != null;
  }

  get triggerMeta(): ReferralTriggerDefinition | null {
    return this.form ? getTriggerDefinition(this.form.Trigger) : null;
  }

  get rewardMeta(): ReferralRewardDefinition | null {
    return this.form ? getRewardDefinition(this.form.RewardType) : null;
  }

  initializeForm() {
    if (this.value && this.hasFormData(this.value)) {
      const trigger = parseReferralTrigger(this.value.Trigger);
      const rewardType = parseReferralRewardType(this.value.RewardType);
      const rewardMeta = rewardType !== null ? getRewardDefinition(rewardType) : null;

      this.form = new UpsertReferralRuleRequest({
        RuleId: this.toNumber(this.value.RuleId, null),
        Trigger: trigger !== null && isReferralRuleUpsertTriggerSupported(trigger)
          ? trigger
          : defaultReferralRuleUpsertTrigger,
        Threshold: this.toNumber(this.value.Threshold, 1),
        WindowDays: this.value.WindowDays == null || this.value.WindowDays === ("" as any)
          ? null
          : this.toNumber(this.value.WindowDays, null),
        RewardType: rewardType !== null ? rewardType : 0,
        RewardDays:
          rewardMeta && !rewardMeta.usesRewardDays
            ? null
            : this.value.RewardDays == null || this.value.RewardDays === ("" as any)
              ? null
              : this.toNumber(this.value.RewardDays, 1),
      });
    } else {
      this.form = new UpsertReferralRuleRequest({
        RuleId: null,
        Trigger: defaultReferralRuleUpsertTrigger,
        Threshold: 1,
        WindowDays: null,
        RewardType: 0,
        RewardDays: 1,
      });
    }
    this.formRenderKey++;
  }

  hasFormData(value: UpsertReferralRuleRequest): boolean {
    return (
      value.RuleId != null ||
      parseReferralTrigger(value.Trigger) !== null ||
      parseReferralRewardType(value.RewardType) !== null
    );
  }

  toNumber(value: any, fallback: number | null): number | null {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  @Watch("value")
  onValueChanged(newValue: UpsertReferralRuleRequest, oldValue: UpsertReferralRuleRequest) {
    if (newValue !== oldValue && this.visible) {
      this.$nextTick(() => {
        this.initializeForm();
      });
    }
  }

  @Watch("visible")
  onVisibleChanged(isVisible: boolean) {
    if (isVisible) {
      this.$nextTick(() => {
        this.initializeForm();
      });
    }
  }

  @Watch("form.Trigger")
  onTriggerChanged(trigger: number) {
    if (!this.form) {
      return;
    }
    // Только устанавливаем defaults при создании нового правила
    if (this.isEditMode) {
      return;
    }
    const meta = getTriggerDefinition(trigger);
    if (!meta) {
      return;
    }
    this.form.Threshold = meta.defaultThreshold;
    this.form.WindowDays = meta.defaultWindowDays;
  }

  @Watch("form.RewardType")
  onRewardTypeChanged(rewardType: number) {
    if (!this.form) {
      return;
    }
    const meta = getRewardDefinition(rewardType);
    if (!meta) {
      return;
    }
    // Очищаем RewardDays если reward type их не требует
    if (!meta.usesRewardDays) {
      this.form.RewardDays = null;
    } else if (this.form.RewardDays == null || this.form.RewardDays === ("" as any)) {
      // Устанавливаем default значение если поле пусто
      this.form.RewardDays = 1;
    }
  }

  onClose() {
    this.$emit("close");
  }

  onOk() {
    const validator = this.$refs.validator as any;
    if (validator && !validator.isValid()) {
      return;
    }

    if (!isReferralRuleUpsertTriggerSupported(this.form.Trigger)) {
      this.$notification.error("Этот тип условия пока не поддерживается сервером.");
      return;
    }

    const payload = buildUpsertReferralRulePayload(this.form);
    const request = new UpsertReferralRuleRequest({
      RuleId: payload.ruleId,
      Trigger: payload.trigger,
      Threshold: payload.threshold,
      WindowDays: payload.windowDays,
      RewardType: payload.rewardType,
      RewardDays: payload.rewardDays,
    });
    this.$emit("save", request);
  }
}
